import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AppModalShell, {
  AppModalButton,
  AppModalContentItem,
} from "../common/AppModalShell";

/**
 * ReportForm modal
 *
 * Props:
 * - isOpen: boolean — controls visibility
 * - onClose: function — called when user closes modal
 * - onSubmit: async function(message: string): Promise<void> | void — called when user presses "Send report"
 */
export default function ReportForm({
  isOpen,
  onClose,
  onSubmit,
  mode = "user",
  loading = false,
}) {
  const { t } = useTranslation("search");

  const isEventReport = mode === "event";
  const isClubReport = mode === "club";
  const isPhotoReport = mode === "photo";

  let title = t("report_form.title_user", "Report this user");
  let subtitle = "";
  let placeholder = t(
    "report_form.placeholder_user",
    "Describe what happened and why you are reporting this user..."
  );

  if (isEventReport) {
    title = t("report_form.title_event", "Report this event");
    subtitle = t(
      "report_form.subtitle_event",
      "Please describe why you are reporting this event."
    );
    placeholder = t(
      "report_form.placeholder_event",
      "Describe what happened and why you are reporting this event..."
    );
  } else if (isClubReport) {
    title = t("report_form.title_club", "Report this club");
    subtitle = t(
      "report_form.subtitle_club",
      "Please describe why you are reporting this club."
    );
    placeholder = t(
      "report_form.placeholder_club",
      "Describe what happened and why you are reporting this club..."
    );
  } else if (isPhotoReport) {
    title = t("report_form.title_photo", "Report profile photo");
    subtitle = t("report_form.subtitle_photo", "Tell us what is wrong with this photo.");
    placeholder = t("report_form.placeholder_photo", "Describe the problem with the photo...");
  }

  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);


  if (!isOpen) return null;

  const handleSubmit = async () => {
    const trimmed = message.trim();
    if (!trimmed || submitting || loading) return;

    try {
      setSubmitting(true);
      setError(null);
      if (onSubmit) {
        const maybePromise = onSubmit(trimmed);
        if (maybePromise && typeof maybePromise.then === "function") {
          await maybePromise;
        }
      }
      // clear and close on success
      setMessage("");
      onClose?.();
    } catch (err) {
      console.error("ReportForm submit error", err);
      setError(
        err?.response?.data?.error ||
          err?.message ||
          t("report_form.error", "Failed to send report. Please try again.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppModalShell
      open={isOpen}
      title={title}
      description={subtitle}
      onClose={onClose}
      closeAriaLabel={t("report_form.close_alt", "Close")}
      zIndexClassName="z-[13150]"
      alignAboveKeyboard
      maxWidthClassName="max-w-md"
      titleClassName="text-[18px] sm:text-2xl"
      descriptionClassName="text-center text-[13px] leading-[18px] text-white/75"
      bodyClassName="px-5 pb-5 pt-1 sm:px-6 sm:pb-6"
    >
      <AppModalContentItem>
        <textarea
          className="min-h-[120px] w-full resize-none rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-base text-white outline-none placeholder:text-white/40 sm:text-sm"
          placeholder={placeholder}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </AppModalContentItem>

      {error ? (
        <AppModalContentItem className="mt-3 text-center text-xs font-semibold text-red-300">
          {error}
        </AppModalContentItem>
      ) : null}

      <AppModalContentItem className="mt-5">
        <AppModalButton
          variant="primary"
          onClick={handleSubmit}
          disabled={!message.trim() || submitting || loading}
          loading={submitting || loading}
          className="text-[#2563EB]"
        >
          {submitting || loading
            ? t("report_form.sending", "Sending...")
            : t("report_form.submit", "Send report")}
        </AppModalButton>
      </AppModalContentItem>
    </AppModalShell>
  );
}
